import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, RotateCcw, ShoppingBag, Sparkles } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { CONCERNS, LEVELS, NEEDS, needsLevel, routineFor } from "@/lib/diagnostic";

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

function Choice({ id, label, hint, onClick }) {
  return (
    <button onClick={onClick} data-testid={`diag-option-${id}`}
      className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-[#D9C8B3] bg-white px-5 py-4 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#7A5418]">
      <span><span className="block text-base font-semibold text-[#141013]">{label}</span>{hint ? <span className="mt-0.5 block text-sm text-[#5E544D]">{hint}</span> : null}</span>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#D9C8B3] text-[#7A5418] group-hover:bg-[#141013] group-hover:text-[#FBF8F3]"><Check className="h-4 w-4" /></span>
    </button>
  );
}

function Step({ n, total, title, children, onBack }) {
  return (
    <div data-testid={`diag-step-${n}`}>
      <div className="flex items-center justify-between text-sm text-[#5E544D]">
        {onBack ? <button onClick={onBack} data-testid="diag-back" className="inline-flex items-center gap-1 hover:text-[#141013]"><ArrowLeft className="h-4 w-4" />Retour</button> : <span />}
        <span>Question {n} / {total}</span>
      </div>
      <div className="mt-3 h-1 rounded-full bg-[#E3D5C3]"><div className="h-1 rounded-full bg-[#7A5418] transition-[width] duration-300" style={{ width: `${(n / total) * 100}%` }} /></div>
      <h2 className="mt-8 font-display text-2xl text-[#141013] md:text-3xl">{title}</h2>
      <div className="mt-6 grid gap-3">{children}</div>
    </div>
  );
}

function Result({ a, onRestart }) {
  const { data: products } = useProducts();
  const { add } = useCart();
  const r = routineFor(a);
  const bySlug = Object.fromEntries((Array.isArray(products) ? products : []).map((p) => [p.slug, p]));
  const units = r.units.map((s) => bySlug[s]).filter(Boolean);
  const pack = r.pack ? bySlug[r.pack] : null;
  const total = units.reduce((s, p) => s + Number(p.price || 0), 0);
  return (
    <div data-testid="diag-result">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#7A5418]"><Sparkles className="h-4 w-4" />Votre routine Ozentya</p>
      <h2 className="mt-3 font-display text-3xl text-[#141013] md:text-4xl">{r.title}</h2>
      <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#5E544D]">{r.text}</p>
      <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {units.map((p) => (
          <li key={p.slug} data-testid={`diag-product-${p.slug}`} className="flex gap-4 rounded-2xl border border-[#E3D5C3] bg-white p-4">
            <img src={p.image} alt={p.name} className="h-20 w-20 shrink-0 rounded-xl object-cover" loading="lazy" />
            <div className="min-w-0"><Link to={`/produit/${p.slug}`} className="font-semibold text-[#141013] hover:underline">{p.name}</Link><p className="mt-1 text-sm text-[#5E544D]">{eur(p.price)}</p></div>
          </li>
        ))}
      </ul>
      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={() => units.forEach((p) => add(p, 1))} disabled={!units.length} data-testid="diag-add-routine"
          className="inline-flex h-14 items-center gap-2 rounded-full bg-[#141013] px-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#FBF8F3] hover:bg-[#3A2A20] disabled:opacity-50">
          <ShoppingBag className="h-4 w-4" />Ajouter la routine · {eur(total)}
        </button>
        <button onClick={onRestart} data-testid="diag-restart" className="inline-flex h-14 items-center gap-2 rounded-full border border-[#141013] px-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#141013]"><RotateCcw className="h-4 w-4" />Refaire le diagnostic</button>
      </div>
      {pack ? (
        <div data-testid="diag-pack" className="mt-8 flex flex-col gap-4 rounded-2xl border-2 border-[#B8893A] bg-[#FFF9EE] p-5 sm:flex-row sm:items-center">
          <img src={pack.image} alt={pack.name} className="h-24 w-24 shrink-0 rounded-xl object-cover" loading="lazy" />
          <div className="flex-1"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A5418]">Notre conseil le plus complet</p><p className="mt-1 font-display text-xl text-[#141013]">{pack.name}</p><p className="text-sm text-[#5E544D]">{eur(pack.price)}</p></div>
          <button onClick={() => add(pack, 1)} data-testid="diag-add-pack" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#7A5418] px-6 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#5E3F10]">Ajouter le pack</button>
        </div>
      ) : null}
      <p className="mt-8 text-xs leading-relaxed text-[#5E544D]">Conseils cosmétiques. En cas de chute de cheveux importante, soudaine ou en plaques, ou d'irritation persistante, consultez un médecin ou un dermatologue.</p>
    </div>
  );
}

export default function Diagnostic() {
  const [a, setA] = useState({});
  useSeo({ title: "Diagnostic cheveux afro gratuit : votre routine en 1 minute", description: "Répondez à quelques questions et recevez la routine Ozentya adaptée à vos cheveux afro, vos locks, votre barbe ou votre peau.", path: "/diagnostic" });
  const set = (k, v) => setA((cur) => ({ ...cur, [k]: v }));
  const back = (k) => setA((cur) => { const n = { ...cur }; delete n[k]; return n; });
  const level = needsLevel(a.need, a.concern);
  const total = a.need === "enfant" ? 1 : level ? 4 : 2;
  let body;
  if (!a.need) body = <Step n={1} total={total} title="Pour quoi cherchez-vous un soin ?">{NEEDS.map((o) => <Choice key={o.id} {...o} onClick={() => set("need", o.id)} />)}</Step>;
  else if (a.need !== "enfant" && !a.concern) body = <Step n={2} total={total} title="Qu'est-ce qui vous préoccupe le plus ?" onBack={() => back("need")}>{CONCERNS[a.need].map((o) => <Choice key={o.id} {...o} onClick={() => set("concern", o.id)} />)}</Step>;
  else if (level && a.fine === undefined) body = <Step n={3} total={total} title="Vos cheveux sont-ils plutôt fins ?" onBack={() => back("concern")}><Choice id="fine-yes" label="Oui, plutôt fins" onClick={() => set("fine", true)} /><Choice id="fine-no" label="Non, normaux à épais" onClick={() => set("fine", false)} /></Step>;
  else if (level && !a.fine && !a.level) body = <Step n={4} total={total} title="Quelle routine vous correspond ?" onBack={() => back("fine")}>{LEVELS.map((o) => <Choice key={o.id} {...o} onClick={() => set("level", o.id)} />)}</Step>;
  else body = <Result a={a} onRestart={() => setA({})} />;
  return (
    <main data-testid="diagnostic-page" className="bg-[#F6F0E7] px-5 pb-24 pt-32 md:px-10 md:pt-40">
      <div className="mx-auto max-w-4xl">
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#7A5418]">Diagnostic gratuit · 1 minute</p>
        <h1 className="font-display text-4xl leading-[1.05] text-[#141013] sm:text-5xl lg:text-6xl">Trouvez <span className="italic text-[#7A5418]">votre routine</span> Ozentya</h1>
        <div className="mt-10 rounded-3xl bg-[#FBF8F3] p-6 shadow-[0_20px_60px_-40px_rgba(20,16,19,0.5)] md:p-10">{body}</div>
      </div>
    </main>
  );
}
